import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { UserRole } from "@/lib/database.types";

interface AuthState {
  session: Session | null;
  user: User | null;
  role: UserRole | null;
  isLoading: boolean;
  isAdmin: boolean;
  isParent: boolean;
}

/**
 * Subscribes to Supabase auth changes and resolves the current user's role.
 *
 * The tricky part is the magic-link callback. When a parent clicks the
 * sign-in link in their email they land at `/parent?code=...` (or with
 * `#access_token=...` in the hash). Supabase's auth client processes that
 * asynchronously — so on first render `getSession()` returns null. If we
 * just set `isLoading = false` at that point, the `RequireParent` route
 * guard fires before processing finishes, redirects to `/parent/login`,
 * and the URL token is wiped.
 *
 * Fix: when we detect an auth callback in the URL, wait for the first
 * `onAuthStateChange` event (signed in, or initial session) before
 * marking ourselves hydrated. Failsafe timeout in case nothing fires.
 */
export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let mounted = true;
    let resolved = false;

    function markHydrated(s: Session | null) {
      if (!mounted || resolved) return;
      resolved = true;
      setSession(s);
      setHydrated(true);
    }

    // Is there an auth token in the URL right now?
    const url = new URL(window.location.href);
    const hash = window.location.hash;
    const hasAuthCallback =
      url.searchParams.has("code") ||
      url.searchParams.has("token_hash") ||
      url.searchParams.has("error_code") ||
      url.searchParams.has("error") ||
      hash.includes("access_token") ||
      hash.includes("error_code") ||
      hash.includes("error=");

    // Is there an auth ERROR in the URL? If so, resolve immediately with no
    // session so the login page can render its error banner. Waiting for
    // onAuthStateChange in this case would just stall for 6s.
    const hasAuthError =
      url.searchParams.has("error_code") ||
      url.searchParams.has("error") ||
      hash.includes("error=") ||
      hash.includes("error_code=");
    if (hasAuthError) {
      console.warn("[useAuth] auth callback contains error — routing to login");
      // Fire and forget; onAuthStateChange won't fire on an errored redirect.
      supabase.auth.getSession().then(({ data }) => markHydrated(data.session));
    }

    // Always listen for auth state changes; this is what fires when the
    // URL token finishes processing.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      if (!resolved) {
        markHydrated(s);
      } else {
        setSession(s);
      }
    });

    if (hasAuthCallback) {
      // Wait for onAuthStateChange. Failsafe at 6s in case something hangs.
      const failsafe = setTimeout(() => {
        if (!resolved) {
          supabase.auth.getSession().then(({ data }) => markHydrated(data.session));
        }
      }, 6000);
      return () => {
        mounted = false;
        sub.subscription.unsubscribe();
        clearTimeout(failsafe);
      };
    }

    // No callback in URL — just check the existing stored session.
    supabase.auth.getSession().then(({ data }) => markHydrated(data.session));

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user?.id ?? null;

  const roleQuery = useQuery({
    queryKey: ["user-role", userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserRole | null> => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) {
        console.warn("[useAuth] role lookup failed:", error.message);
        return null;
      }
      return data?.role ?? null;
    },
  });

  const role = roleQuery.data ?? null;
  return {
    session,
    user: session?.user ?? null,
    role,
    isLoading: !hydrated || (!!userId && roleQuery.isLoading),
    isAdmin: role === "admin",
    isParent: role === "parent",
  };
}

/** Convenience: sign out and redirect to /. */
export async function signOut() {
  await supabase.auth.signOut();
  window.location.assign("/");
}
